// Camping Australia Mega Menu Component v8.0 - image panels
// ONE file, byte-identical on both surfaces:
//   theme:        assets/enhanced-mega-menu-component.js   (window.CampingAustraliaMegaMenu(React))
//   trip planner: client-app/app/lib/enhanced-mega-menu-component.js (imported by MegaMenuWrapper.tsx)
// Props (all optional): menuData (array; defaults to window.customMenuData),
// storeOrigin ('' on the store; 'https://campingaustralia.com.au' in the planner
// so relative links and the live-count fetch point back at the store).
//
// Desktop: a full-width panel per top-level item. Left: title, "Shop all (n)",
// a note and the category list. Middle: image tiles ("Featured", the default) or
// the hovered/focused category's full link list. Right: a featured guide.
// Hover intent: 110ms to open, instant switching once open, 200ms grace to leave.
// Categories commit only when the pointer PAUSES on them (every mousemove resets
// a 110ms timer), so a diagonal move toward a link never flips the panel.
// Keyboard: the chevron button opens a panel (aria-expanded); Tab walks each
// category then its links, then the next category; arrows move between
// categories; Escape closes and returns focus to the chevron.
(function (global) {
  'use strict';

  function factory(R) {
    R = R || (typeof window !== 'undefined' ? window.React : null);
    if (!R) { if (typeof console !== 'undefined') console.error('Mega menu: React not available'); return function () { return null; }; }
    var h = R.createElement;
    var useState = R.useState, useEffect = R.useEffect, useRef = R.useRef, useCallback = R.useCallback;

    function readData() {
      var d = typeof window !== 'undefined' && window.customMenuData && window.customMenuData.data;
      return d || {};
    }
    function fmt(n) { return typeof n === 'number' && n > 0 ? n.toLocaleString('en-AU') : ''; }
    function imgAt(u, w) { return u + (u.indexOf('?') === -1 ? '?' : '&') + 'width=' + w; }
    function srcset(u, ws) { return ws.map(function (w) { return imgAt(u, w) + ' ' + w + 'w'; }).join(', '); }
    var chev = function () {
      return h('svg', { width: 12, height: 12, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2.4, strokeLinecap: 'round', strokeLinejoin: 'round', 'aria-hidden': 'true', focusable: 'false' }, h('path', { d: 'm6 9 6 6 6-6' }));
    };
    var arrow = function () { return h('span', { className: 'ca-mega__arrow', 'aria-hidden': 'true' }, '→'); };
    function focusables(el) { return el ? Array.prototype.slice.call(el.querySelectorAll('a[href], button:not([disabled])')) : []; }

    var liveCountsPromise = null;

    return function MegaMenu(props) {
      props = props || {};
      var origin = props.storeOrigin || '';
      var data = readData();
      var menuData = props.menuData || data.megamenu || [];
      var panels = (props.panels || data.panels) || {};

      var _a = useState(null), activeMenu = _a[0], setActiveMenu = _a[1];
      var _b = useState(null), activeCategory = _b[0], setActiveCategory = _b[1];
      var _c = useState(false), isMobile = _c[0], setIsMobile = _c[1];
      var _d = useState(false), mobileMenuOpen = _d[0], setMobileMenuOpen = _d[1];
      var _e = useState(function () { return new Set(); }), mobileExpanded = _e[0], setMobileExpanded = _e[1];
      var _f = useState(function () { return {}; }), visited = _f[0], setVisited = _f[1];
      var _g = useState(function () { return {}; }), liveCounts = _g[0], setLiveCounts = _g[1];

      var openTimer = useRef(null);
      var closeTimer = useRef(null);
      var catTimer = useRef(null);
      var pendingFocus = useRef(null);
      var navRef = useRef(null);
      var lockedY = useRef(0);
      var activeRef = useRef(null);
      activeRef.current = activeMenu;

      // ---------- URLs ----------
      var href = useCallback(function (u) {
        if (!u) return '#';
        if (typeof u === 'string') return u.charAt(0) === '/' ? origin + u : u;
        var t = u.type && u.type.id;
        if (t === 'collection' && u.collection) return origin + '/collections/' + u.collection.handle;
        if (t === 'home') return origin + '/';
        if (t === 'blog' && u.blog) return origin + '/blogs/' + u.blog.handle;
        if (t === 'page' && u.page) return origin + '/pages/' + u.page.handle;
        if (t === 'external' && u.external_url) return u.external_url;
        return '#';
      }, [origin]);
      var handleOf = function (u) { return u && typeof u === 'object' && u.collection ? u.collection.handle : null; };

      // ---------- live counts (once per page, on first use) ----------
      var loadCounts = useCallback(function () {
        if (typeof window === 'undefined' || typeof fetch !== 'function') return;
        // Cross-origin (the planner on its own domain) has no CORS on the store: keep the snapshot.
        if (origin && window.location && origin.indexOf(window.location.hostname) === -1) return;
        if (!liveCountsPromise) {
          liveCountsPromise = fetch('/collections/all?view=mega-counts', { credentials: 'same-origin' })
            .then(function (r) { return r.ok ? r.text() : null; })
            .then(function (t) { try { return t ? JSON.parse(t) : null; } catch (e) { return null; } })
            .catch(function () { return null; });
        }
        liveCountsPromise.then(function (j) { if (j && j.ok) setLiveCounts(j); });
      }, [origin]);

      // ---------- desktop open / close ----------
      var clearTimers = function () { clearTimeout(openTimer.current); clearTimeout(closeTimer.current); };
      var markVisited = useCallback(function (id) {
        setVisited(function (v) { if (v[id]) return v; var n = Object.assign({}, v); n[id] = true; return n; });
      }, []);
      var openMenu = useCallback(function (id) {
        clearTimers();
        markVisited(id);
        if (activeRef.current !== id) setActiveCategory(null);
        setActiveMenu(id);
        loadCounts();
        if (typeof window !== 'undefined' && typeof window.gtag === 'function') window.gtag('event', 'menu_open', { event_category: 'navigation', event_label: id });
      }, [markVisited, loadCounts]);
      var closeMenu = useCallback(function (returnFocusTo) {
        clearTimers();
        clearTimeout(catTimer.current);
        setActiveMenu(null);
        setActiveCategory(null);
        if (returnFocusTo && navRef.current) {
          var btn = navRef.current.querySelector('[aria-controls="ca-mega-' + returnFocusTo + '"]');
          if (btn) btn.focus();
        }
      }, []);
      var onItemEnter = function (id) {
        markVisited(id); // mount the (hidden) panel now so its images start loading during the intent delay
        clearTimers();
        openTimer.current = setTimeout(function () { openMenu(id); }, activeMenu ? 0 : 110);
      };
      var onItemLeave = function () {
        clearTimeout(openTimer.current);
        closeTimer.current = setTimeout(function () { closeMenu(); }, 200);
      };

      // ---------- categories ----------
      var hoverCat = function (key) {
        clearTimeout(catTimer.current);
        catTimer.current = setTimeout(function () { setActiveCategory(key); }, 110);
      };
      var leaveCat = function () { clearTimeout(catTimer.current); };
      var selectCat = function (key) { clearTimeout(catTimer.current); setActiveCategory(key); };

      // After a keyboard move into a category's list, focus its first/last link.
      useEffect(function () {
        var want = pendingFocus.current;
        if (!want || !activeMenu || !navRef.current) return;
        pendingFocus.current = null;
        var els = focusables(navRef.current.querySelector('#ca-mega-' + activeMenu + ' .ca-mega__main'));
        var el = want === 'first' ? els[0] : els[els.length - 1];
        if (el) el.focus();
      }, [activeCategory, activeMenu]);

      // ---------- mobile ----------
      var lockScroll = useCallback(function () {
        if (typeof document === 'undefined') return;
        lockedY.current = window.scrollY || 0;
        var s = document.body.style;
        s.position = 'fixed'; s.top = '-' + lockedY.current + 'px'; s.left = '0'; s.right = '0'; s.width = '100%'; s.overflow = 'hidden';
      }, []);
      var unlockScroll = useCallback(function () {
        if (typeof document === 'undefined') return;
        var s = document.body.style;
        if (s.position !== 'fixed') return;
        s.position = ''; s.top = ''; s.left = ''; s.right = ''; s.width = ''; s.overflow = '';
        window.scrollTo(0, lockedY.current);
      }, []);
      var closeMobile = useCallback(function () {
        setMobileMenuOpen(false);
        unlockScroll();
        setMobileExpanded(new Set());
      }, [unlockScroll]);
      var toggleMobile = useCallback(function () {
        setMobileMenuOpen(function (prev) {
          var next = !prev;
          if (next) { lockScroll(); loadCounts(); } else unlockScroll();
          return next;
        });
      }, [lockScroll, unlockScroll, loadCounts]);
      var toggleMobileItem = function (id) {
        setMobileExpanded(function (prev) { var n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
      };

      useEffect(function () {
        if (typeof window === 'undefined') return;
        var t;
        var onResize = function () {
          clearTimeout(t);
          t = setTimeout(function () {
            var m = window.innerWidth < 1024;
            setIsMobile(m);
            if (!m && mobileMenuOpen) closeMobile();
          }, 100);
        };
        setIsMobile(window.innerWidth < 1024);
        window.addEventListener('resize', onResize);
        return function () { window.removeEventListener('resize', onResize); clearTimeout(t); };
      }, [mobileMenuOpen, closeMobile]);

      // The header's burger (.toggle-menu) opens the mobile panel.
      useEffect(function () {
        if (typeof document === 'undefined') return;
        var onClick = function (e) {
          var t = e.target;
          if (!t || !t.closest) return;
          if (t.closest('.filter-button') || t.closest('[data-action="open-filters"]')) return;
          if (t.closest('.toggle-menu') || t.closest('[data-action="toggle-browse"]')) {
            e.preventDefault(); e.stopPropagation(); toggleMobile();
          }
        };
        document.addEventListener('click', onClick, true);
        return function () { document.removeEventListener('click', onClick, true); };
      }, [toggleMobile]);

      useEffect(function () {
        if (typeof document === 'undefined') return;
        var onKey = function (e) {
          if (e.key !== 'Escape') return;
          if (mobileMenuOpen) closeMobile();
          if (activeMenu) closeMenu(activeMenu);
        };
        document.addEventListener('keydown', onKey);
        return function () { document.removeEventListener('keydown', onKey); };
      }, [mobileMenuOpen, activeMenu, closeMobile, closeMenu]);

      useEffect(function () {
        return function () { clearTimers(); clearTimeout(catTimer.current); unlockScroll(); };
        // eslint-disable-next-line
      }, []);

      // ---------- shared pieces ----------
      // Live counts (when fetched) over the snapshot shipped in the data file.
      var countOf = function (key) { if (!key) return undefined; return liveCounts[key] !== undefined ? liveCounts[key] : (data.counts || {})[key]; };
      var productsLabel = function (n) { return fmt(n) ? fmt(n) + (n === 1 ? ' product' : ' products') : ''; };
      var titleOf = function (item) { return (item.setting && item.setting.title) || ''; };
      var isCampsite = function (item) { var t = titleOf(item); return item.id === 'campsite-menu' || item.id === 'find-campsites' || t.indexOf('Campsite') !== -1 || t.indexOf('Find') !== -1; };

      function entriesFor(item, panel) {
        var list = [];
        if (panel && (panel.tiles || panel.logos)) list.push({ key: '__picks', title: panel.picks || 'Featured', picks: true });
        (item.menus || []).forEach(function (c) {
          var u = c.setting && c.setting.url;
          list.push({ key: c.id, title: titleOf(c), url: u ? href(u) : null, count: countOf(handleOf(u)), items: c.menus || [] });
        });
        return list;
      }

      function tileImg(u, ws, sizes, w, hgt) {
        return h('img', { src: imgAt(u, ws[ws.length - 1]), srcSet: srcset(u, ws), sizes: sizes, width: w, height: hgt, loading: 'lazy', decoding: 'async', alt: '' });
      }

      function renderTiles(panel) {
        if (panel.logos) {
          return h('ul', { className: 'ca-mega__logos' }, panel.logos.map(function (l) {
            return h('li', { key: l.handle },
              h('a', { className: 'ca-mega__logo', href: href(l.url) },
                h('span', { className: 'ca-mega__logo-img' }, h('img', { src: imgAt(l.img, 320), srcSet: srcset(l.img, [200, 320]), sizes: '160px', width: 160, height: 80, loading: 'lazy', decoding: 'async', alt: l.title })),
                h('span', { className: 'ca-mega__logo-count' }, productsLabel(countOf(l.handle)))));
          }));
        }
        return h('ul', { className: 'ca-mega__tiles' }, panel.tiles.map(function (t) {
          return h('li', { key: t.handle + t.title },
            h('a', { className: 'ca-mega__tile', href: href(t.url) },
              h('span', { className: 'ca-mega__tile-img' }, tileImg(t.img, [200, 300, 400], '(min-width: 1200px) 150px, 130px', 200, 200)),
              h('span', { className: 'ca-mega__tile-name' }, t.title),
              h('span', { className: 'ca-mega__tile-count' }, productsLabel(countOf(t.handle)))));
        }));
      }

      function renderFeature(f, key) {
        return h('a', { key: key, className: 'ca-mega__feature', href: href(f.url) },
          h('span', { className: 'ca-mega__feature-img' }, h('img', { src: imgAt(f.img, 600), srcSet: srcset(f.img, [400, 600, 800]), sizes: '300px', width: 600, height: 450, loading: 'lazy', decoding: 'async', alt: '' })),
          h('span', { className: 'ca-mega__feature-label' }, f.label),
          h('span', { className: 'ca-mega__feature-title' }, f.title),
          h('span', { className: 'ca-mega__feature-more' }, 'Read the guide ', arrow()));
      }

      function renderPanel(item, panel, isOpen) {
        var id = item.id;
        var title = (panel && panel.title) || titleOf(item);
        var entries = entriesFor(item, panel);
        var current = isOpen && activeCategory ? activeCategory : (entries[0] ? entries[0].key : null);
        var active = entries.filter(function (e) { return e.key === current; })[0];
        var kind = panel && panel.logos ? 'brands' : panel && panel.features ? 'resources' : 'shop';
        var allN = panel ? countOf(panel.handle) : undefined;

        var onCatKey = function (e, idx) {
          var main = e.currentTarget.closest('.ca-mega__inner').querySelector('.ca-mega__main');
          var cats = focusables(e.currentTarget.closest('.ca-mega__cats'));
          if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();
            var n = cats[(idx + (e.key === 'ArrowDown' ? 1 : cats.length - 1)) % cats.length];
            if (n) n.focus();
          } else if (e.key === 'Tab' && !e.shiftKey) {
            var first = focusables(main)[0];
            if (first) { e.preventDefault(); first.focus(); }
          } else if (e.key === 'Tab' && e.shiftKey && idx > 0) {
            e.preventDefault();
            pendingFocus.current = 'last';
            selectCat(entries[idx - 1].key);
            if (entries[idx - 1].key === current) { var l = focusables(main); if (l.length) l[l.length - 1].focus(); pendingFocus.current = null; }
          }
        };
        var onMainKey = function (e) {
          if (e.key !== 'Tab') return;
          var els = focusables(e.currentTarget);
          var idx = entries.map(function (x) { return x.key; }).indexOf(current);
          var cats = focusables(e.currentTarget.closest('.ca-mega__inner').querySelector('.ca-mega__cats'));
          if (!e.shiftKey && e.target === els[els.length - 1] && idx < entries.length - 1 && cats[idx + 1]) {
            e.preventDefault(); cats[idx + 1].focus();
          } else if (e.shiftKey && e.target === els[0] && cats[idx]) {
            e.preventDefault(); cats[idx].focus();
          }
        };

        var aside = h('div', { className: 'ca-mega__aside' },
          h('p', { className: 'ca-mega__title' }, title),
          panel && panel.all && panel.url ? h('a', { className: 'ca-mega__all', href: href(panel.url) }, panel.all, fmt(allN) ? h('span', { className: 'ca-mega__num' }, ' (' + fmt(allN) + ')') : null, ' ', arrow()) : null,
          panel && panel.note ? h('p', { className: 'ca-mega__note' }, panel.note) : null,
          entries.length > 1 ? h('ul', { className: 'ca-mega__cats', 'aria-label': title + ' categories' }, entries.map(function (en, i) {
            var cls = 'ca-mega__cat' + (en.key === current ? ' is-active' : '') + (en.picks ? ' is-picks' : '');
            var common = {
              className: cls,
              onMouseEnter: function () { hoverCat(en.key); },
              onMouseMove: function () { hoverCat(en.key); },
              onMouseLeave: leaveCat,
              onFocus: function () { selectCat(en.key); },
              onKeyDown: function (e) { onCatKey(e, i); },
              'aria-current': en.key === current ? 'true' : undefined
            };
            var inner = [h('span', { key: 't', className: 'ca-mega__cat-name' }, en.title), en.count >= 10 ? h('span', { key: 'n', className: 'ca-mega__cat-count' }, fmt(en.count)) : null, h('span', { key: 'c', className: 'ca-mega__cat-chev', 'aria-hidden': 'true' }, '›')];
            return h('li', { key: en.key },
              en.url && en.url !== '#' && !en.picks ? h('a', Object.assign({ href: en.url }, common), inner) : h('button', Object.assign({ type: 'button' }, common), inner));
          })) : null,
          panel && panel.links ? h('ul', { className: 'ca-mega__links' }, panel.links.map(function (l) {
            return h('li', { key: l.url }, h('a', { href: href(l.url) }, h('span', null, l.title), fmt(countOf(l.handle)) ? h('span', { className: 'ca-mega__cat-count' }, fmt(countOf(l.handle))) : null));
          })) : null);

        var main;
        if (kind === 'resources') {
          main = h('div', { className: 'ca-mega__main ca-mega__features' }, panel.features.map(function (f, i) { return renderFeature(f, i); }));
        } else if (active && active.picks) {
          main = h('div', { className: 'ca-mega__main', onKeyDown: onMainKey }, renderTiles(panel));
        } else if (active) {
          main = h('div', { className: 'ca-mega__main', onKeyDown: onMainKey },
            h('div', { className: 'ca-mega__list-head' },
              h('p', { className: 'ca-mega__list-title' }, active.title),
              active.url && active.url !== '#' ? h('a', { className: 'ca-mega__list-all', href: active.url }, 'Shop all', fmt(active.count) ? ' (' + fmt(active.count) + ')' : '', ' ', arrow()) : null),
            h('ul', { className: 'ca-mega__list' }, active.items.map(function (s) {
              return h('li', { key: s.id }, h('a', { href: href(s.setting && s.setting.url) }, titleOf(s)));
            })));
        } else {
          main = h('div', { className: 'ca-mega__main' });
        }

        return h('div', {
          id: 'ca-mega-' + id,
          className: 'ca-mega' + (isOpen ? ' is-open' : ''),
          role: 'region',
          'aria-label': titleOf(item),
          onMouseEnter: function () { clearTimeout(closeTimer.current); },
          onMouseLeave: onItemLeave
        },
          h('div', { className: 'ca-mega__inner ca-mega__inner--' + kind + (panel && panel.feature ? ' has-feature' : '') },
            aside, main,
            panel && panel.feature && kind === 'shop' ? h('div', { className: 'ca-mega__side' }, renderFeature(panel.feature, 'f')) : null));
      }

      // ---------- mobile ----------
      function renderMobileItem(item, level) {
        var hasSub = item.menus && item.menus.length > 0;
        var panel = level === 0 ? panels[item.id] : null;
        var expandable = hasSub || !!panel;
        var expanded = mobileExpanded.has(item.id);
        var title = titleOf(item) || 'Menu';
        var camp = isCampsite(item);
        var u = href((panel && panel.url) || (item.setting && item.setting.url));
        var thumbSrc = panel ? (panel.thumb || (panel.feature && panel.feature.img) || (panel.features && panel.features[0].img) || (panel.tiles && panel.tiles[0].img) || null) : null;
        var cls = 'mobile-menu-item level-' + level + (camp ? ' campsite-item' : '') + (panel ? ' has-panel' : '');

        var head = expandable
          ? h('div', { className: 'mobile-menu-item-wrapper' },
              h(u !== '#' ? 'a' : 'button', u !== '#' ? { href: u, className: 'mobile-menu-link' } : { type: 'button', className: 'mobile-menu-link', onClick: function () { toggleMobileItem(item.id); } },
                thumbSrc ? h('span', { className: 'ca-m-thumb' }, h('img', { src: imgAt(thumbSrc, 120), width: 40, height: 40, loading: 'lazy', decoding: 'async', alt: '' })) : null,
                h('span', { className: 'ca-m-label' }, title)),
              h('button', { type: 'button', className: 'mobile-expand-btn' + (expanded ? ' expanded' : ''), onClick: function () { toggleMobileItem(item.id); }, 'aria-expanded': expanded ? 'true' : 'false', 'aria-label': (expanded ? 'Collapse ' : 'Expand ') + title }, expanded ? '-' : '+'))
          : h('a', { href: u, className: 'mobile-menu-link' + (camp ? ' campsite-link' : ''), onClick: camp ? undefined : closeMobile }, title);

        var body = null;
        if (expandable && expanded) {
          var kids = [];
          if (panel && panel.all && panel.url) {
            kids.push(h('li', { key: 'all', className: 'mobile-menu-item level-1' }, h('a', { href: href(panel.url), className: 'mobile-viewall-link', onClick: closeMobile }, panel.all, fmt(countOf(panel.handle)) ? ' (' + fmt(countOf(panel.handle)) + ')' : '')));
          } else if (!panel && u !== '#') {
            kids.push(h('li', { key: 'all', className: 'mobile-menu-item level-' + (level + 1) }, h('a', { href: u, className: 'mobile-viewall-link', onClick: closeMobile }, 'View all ' + title)));
          }
          if (panel && (panel.tiles || panel.logos)) {
            var row = (panel.tiles || panel.logos);
            kids.push(h('li', { key: 'tiles', className: 'ca-m-tiles-row' },
              h('ul', { className: 'ca-m-tiles' + (panel.logos ? ' is-logos' : '') }, row.map(function (t) {
                return h('li', { key: t.handle + t.title }, h('a', { href: href(t.url), className: 'ca-m-tile', onClick: closeMobile },
                  h('span', { className: 'ca-m-tile-img' }, h('img', { src: imgAt(t.img, panel.logos ? 240 : 200), width: 100, height: panel.logos ? 50 : 100, loading: 'lazy', decoding: 'async', alt: panel.logos ? t.title : '' })),
                  panel.logos ? null : h('span', { className: 'ca-m-tile-name' }, t.title)));
              }))));
          }
          if (panel && panel.links) {
            panel.links.forEach(function (l) {
              kids.push(h('li', { key: l.url, className: 'mobile-menu-item level-1' }, h('a', { href: href(l.url), className: 'mobile-menu-link', onClick: closeMobile }, l.title)));
            });
          }
          (item.menus || []).forEach(function (s) { kids.push(renderMobileItem(s, level + 1)); });
          var feats = panel ? (panel.feature ? [panel.feature] : panel.features || []) : [];
          feats.forEach(function (f, i) {
            kids.push(h('li', { key: 'f' + i, className: 'ca-m-feature-row' }, h('a', { href: href(f.url), className: 'ca-m-feature', onClick: closeMobile },
              h('span', { className: 'ca-m-feature-img' }, h('img', { src: imgAt(f.img, 240), width: 96, height: 72, loading: 'lazy', decoding: 'async', alt: '' })),
              h('span', { className: 'ca-m-feature-txt' }, h('span', { className: 'ca-m-feature-label' }, f.label), h('span', { className: 'ca-m-feature-title' }, f.title)))));
          });
          body = h('ul', { className: 'mobile-submenu' }, kids);
        }
        return h('li', { key: item.id, className: cls }, head, body);
      }

      // No data yet: the empty 48px bar, so nothing shifts when the items arrive.
      if (!menuData.length) return h('div', { className: 'ca-enhanced-menu' }, h('div', { className: 'ca-menu-container' }, h('nav', { className: 'ca-main-navigation', 'aria-label': 'Shop categories' })));

      if (isMobile) {
        return h('div', { className: 'ca-mobile-menu-wrapper' + (mobileMenuOpen ? ' menu-open' : '') },
          mobileMenuOpen ? h('div', { className: 'ca-mobile-overlay', onClick: closeMobile }) : null,
          h('div', { className: 'ca-mobile-menu-panel' + (mobileMenuOpen ? ' open' : ''), role: 'dialog', 'aria-modal': mobileMenuOpen ? 'true' : undefined, 'aria-label': 'Menu' },
            h('div', { className: 'ca-mobile-menu-header' },
              h('h2', null, 'Menu'),
              h('button', { type: 'button', className: 'ca-mobile-close-btn', onClick: closeMobile, 'aria-label': 'Close menu' }, 'x')),
            h('div', { className: 'ca-mobile-menu-content' },
              h('ul', { className: 'ca-mobile-menu-list' }, menuData.map(function (it) { return renderMobileItem(it, 0); })))));
      }

      return h('div', { className: 'ca-enhanced-menu' + (activeMenu ? ' has-open' : '') },
        h('div', { className: 'ca-menu-container' },
          h('nav', { className: 'ca-main-navigation', 'aria-label': 'Shop categories', ref: navRef },
            menuData.map(function (item) {
              var panel = panels[item.id];
              var hasPanel = !!panel || (item.menus && item.menus.length > 0);
              var isOpen = activeMenu === item.id;
              var title = titleOf(item) || 'Menu';
              var camp = isCampsite(item);
              var u = href((panel && panel.url) || (item.setting && item.setting.url));
              var cls = 'ca-nav-item' + (hasPanel ? ' ca-has-dropdown' : '') + (isOpen ? ' ca-active' : '') + (camp ? ' ca-campsite' : '');
              if (!hasPanel) return h('div', { key: item.id, className: cls }, h('a', { href: u, className: 'ca-nav-link' }, title));
              return h('div', {
                key: item.id, className: cls,
                onMouseEnter: function () { onItemEnter(item.id); },
                onMouseLeave: onItemLeave,
                onBlur: function (e) { if (isOpen && !(e.relatedTarget && e.currentTarget.contains(e.relatedTarget))) closeMenu(); }
              },
                u !== '#'
                  ? h('a', { href: u, className: 'ca-nav-link' }, title)
                  : h('button', { type: 'button', className: 'ca-nav-link', tabIndex: -1, 'aria-hidden': 'true', onClick: function () { if (isOpen) closeMenu(); else openMenu(item.id); } }, title),
                h('button', {
                  type: 'button', className: 'ca-nav-toggle',
                  'aria-expanded': isOpen ? 'true' : 'false', 'aria-controls': 'ca-mega-' + item.id,
                  'aria-label': title + ' menu',
                  onClick: function (e) {
                    if (isOpen) { closeMenu(); return; }
                    openMenu(item.id);
                    if (e.detail === 0) setTimeout(function () { var el = navRef.current && navRef.current.querySelector('#ca-mega-' + item.id); var f = focusables(el)[0]; if (f) f.focus(); }, 30);
                  }
                }, chev()),
                visited[item.id] || isOpen ? renderPanel(item, panel, isOpen) : null);
            }))));
    };
  }

  if (typeof window !== 'undefined') {
    window.MegaMenu = factory;
    window.CampingAustraliaMegaMenu = factory;
    window.createMegaMenu = factory;
  }
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = factory;
    module.exports.default = factory;
    module.exports.createMegaMenu = factory;
  }
})(typeof globalThis !== 'undefined' ? globalThis : this);
